import AutocompleteWrapper from '@/components/AutocompleteWrapper/AutocompleteWrapper'
import { TRAVELER_SELECTION_COLLISION_AVOIDANCE } from '@/components/SearchForm/constants'
import HorizonBundleAndSave from '@/components/SearchFormExperiment/utils/HorizonBundleAndSave'
import type { GA4PageNameType } from '@/ga4types'
import useTabGroup from '@/hooks/useTabGroup'
import { getDateFieldErrorMessage } from '@/shared-utils/error-helpers'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'
import type { CABIN_CLASS_TYPE, FLIGHT_TYPE, LOCATION_SEARCH_TYPE, TripType } from '@/types'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  cn,
  DatePicker,
  RadioGroup,
  Select,
  TRANSFORMER_MAX_MOBILE_WIDTH_PX,
  TravelerSelection,
  type ITravelerInfo,
} from '@pcln/horizon'
import { HorizonFlyDatePicker } from '@pcln/horizon-fly-date-picker'
import { addDays } from 'date-fns/addDays'
import { addMonths } from 'date-fns/addMonths'
import { type ParsedUrlQuery } from 'node:querystring'
import { Fragment, useCallback, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import searchFormSchema from '../SearchForm/validation'
import { MULTI_DESTINATION, ONE_WAY, ROUND_TRIP } from '../constants'
import { getFlightTypeClass, getGridContainerClass, getInputClass } from './HorizonFlightForm.variants'
import { CABIN_OPTIONS, DEFAULT_VALUES, LABELS, PLACEHOLDERS } from './constants'
import useFlightFormSubmitValues from './useFlightFormSubmitValues'
import useInitialFlightFormValues from './useInitialFlightFormValues'

import { useMediaQuery } from 'react-responsive'
import { isRoundTrip } from '../SearchForm/utils'
import type { FlightFormStateType } from '../types'
import {
  addFlightOnClick,
  createRadioOptions,
  getMultiDestDefaultMonth,
  getMultiDestDisabledDates,
  getMultiDestinationSelectedDate,
  getSelectedDates,
  onCalendarChange,
} from './utils/formHandlers'

type FlightSearchFormProps = {
  isRebook?: boolean
  isModify?: boolean
  onSubmit: (pageName: GA4PageNameType, data: FlightFormStateType, queryParams?: ParsedUrlQuery) => void
  pageName?: GA4PageNameType
  useCompactLayout?: boolean
}

export default function FlightSearchForm({
  isRebook = false,
  isModify = false,
  onSubmit,
  pageName = 'homepage',
  useCompactLayout = false,
}: FlightSearchFormProps) {
  const { initialValues, query, isMobile, cguid, appName, appVersion } = useInitialFlightFormValues(
    isRebook,
    isModify,
  )

  const isMobileComponent = useMediaQuery({ maxWidth: TRANSFORMER_MAX_MOBILE_WIDTH_PX })
  const {
    control,
    setValue,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = useForm<FlightFormStateType>({
    defaultValues: initialValues,
    mode: 'onSubmit',
    resolver: yupResolver(searchFormSchema),
    reValidateMode: 'onChange',
  })

  const { watchFlightType, watchFlights, watchCabinClass, watchTravelers, watchTripType } =
    useFlightFormSubmitValues({
      control,
      clearErrors,
      initialValues,
    })

  const flightDetails = {
    cabinClass: watchCabinClass || DEFAULT_VALUES.CABIN_CLASS,
    tripType: watchFlightType || DEFAULT_VALUES.FLIGHT_TYPE,
    flights: [
      {
        departureAirport: {
          id: watchFlights[0]?.startLocation?.id || '',
          cityCode: watchFlights[0]?.startLocation?.cityCode || '',
        },
        arrivalAirport: {
          id: watchFlights[0]?.endLocation?.id || '',
          cityCode: watchFlights[0]?.endLocation?.cityCode || '',
        },
      },
    ],
  }

  const [addHotel, setAddHotel] = useState<boolean>(DEFAULT_VALUES.ADD_HOTEL)
  const [addCar, setAddCar] = useState<boolean>(DEFAULT_VALUES.ADD_CAR)
  const { registerTabElement, focusNextElement } = useTabGroup()

  const flights = watchFlights || [
    {
      startLocation: {},
      endLocation: {},
      startDate: '',
      endDate: '',
    },
  ]

  const isPackage = addHotel || addCar

  const onItemSelect = useCallback(
    (
      searchKey: `flights.${number}.startLocation` | `flights.${number}.endLocation`,
      item?: LOCATION_SEARCH_TYPE,
    ) => {
      setValue(searchKey, item)
      if (!isMobile && !!item && typeof focusNextElement === 'function') {
        focusNextElement(true)
      } else if (isMobile && !!item && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    },
    [setValue, isMobile, focusNextElement],
  )

  const [bundleSelections, setBundleSelections] = useState<{
    [key: string]: boolean
  }>({
    STAY: addHotel,
    DRIVE: addCar,
  })

  const handleBundleSelectionChange = useCallback(
    (updatedTripType: string, checked: boolean, optionValue: string) => {
      setBundleSelections((prev) => ({
        ...prev,
        [optionValue]: checked,
      }))
      setValue('tripType', updatedTripType as TripType)
      if (optionValue === 'STAY') {
        setAddHotel(checked)
        const newRooms = checked ? 1 : 0
        setValue('travelers', {
          ...watchTravelers,
          rooms: newRooms,
        })
      } else if (optionValue === 'DRIVE') {
        setAddCar(checked)
      }
    },
    [setValue, watchTravelers],
  )
  const currentDate = new Date()
  const calendarMaxMonth = addMonths(currentDate, 11)

  const [maxSelectableDate, setMaxSelectableDate] = useState<Date | undefined>()
  const [defaultMonth, setDefaultMonth] = useState<Date>(currentDate)

  return (
    <div className='w-auto'>
      <form
        onSubmit={handleSubmit((values: FlightFormStateType) => {
          onSubmit?.(pageName, values, query)
          logSearchFormAnalytics(cguid, pageName, 'flights', isMobile)
        })}
        className='w-auto'
        aria-label='horizon-flight-search-form'
      >
        <div className='mx-auto w-auto'>
          <div className={getGridContainerClass(watchFlightType, addHotel, isPackage, useCompactLayout)}>
            {/* Flight Type Radio Buttons */}
            <div className={getFlightTypeClass(watchFlightType || DEFAULT_VALUES.FLIGHT_TYPE)}>
              <RadioGroup
                className='flex-row gap-1'
                name='flightType'
                options={createRadioOptions()}
                defaultValue='round-trip'
                required
                aria-labelledby='flight-type'
                handleChange={(value: unknown) => {
                  setValue('flightType', value as TripType)
                }}
              />
              {/* Cabin class dropdown - conditionally rendered (hidden in compact mode) */}
              {!isPackage && !useCompactLayout && (
                <div className='hidden lg:block'>
                  <Select
                    size='sm'
                    label={LABELS.CABIN_CLASS}
                    menuItems={[...CABIN_OPTIONS]}
                    initialValue={watchCabinClass || DEFAULT_VALUES.CABIN_CLASS}
                    onChange={(value: string) => {
                      setValue('cabinClass', value as CABIN_CLASS_TYPE, {
                        shouldDirty: true,
                      })
                    }}
                    placeholder={PLACEHOLDERS.CABIN_CLASS}
                    isLabelHidden
                  />
                </div>
              )}
            </div>

            {/* Multi-destination flights rendering */}
            {watchFlightType === MULTI_DESTINATION ? (
              <>
                {flights.map((flight, index) => (
                  <Fragment key={`flight-${flight.startLocation?.id || flight.endLocation?.id || index}`}>
                    <div className='col-span-full flex items-center gap-4'>
                      <span className='text-base lg:text-sm font-bold'>Flight {index + 1}</span>
                      {index > 0 && (
                        <Button
                          type='tertiary'
                          size='sm'
                          buttonType='button'
                          className='**:text-xs min-h-auto **:font-medium'
                          onClick={() => {
                            // Remove flight from form state
                            const updatedFlights = [...flights.slice(0, index), ...flights.slice(index + 1)]
                            setValue('flights', updatedFlights)
                          }}
                          aria-label={`Remove flight ${index + 1}`}
                        >
                          Remove
                        </Button>
                      )}
                    </div>

                    {/* Origin Input */}
                    <AutocompleteWrapper
                      searchKey='startLocation'
                      label='Departing from?'
                      typeAheadEndpoint='flights'
                      errorMessage={errors.flights?.[index]?.startLocation?.message}
                      initialSelectedItem={flight.startLocation}
                      onItemSelect={(itemSelected) => {
                        onItemSelect(`flights.${index}.startLocation`, itemSelected ?? undefined)
                      }}
                      ref={(elem: HTMLInputElement) => {
                        registerTabElement(elem)
                      }}
                    />

                    {/* Destination Input */}
                    <AutocompleteWrapper
                      searchKey='endLocation'
                      label='Going to?'
                      typeAheadEndpoint='flights'
                      errorMessage={errors.flights?.[index]?.endLocation?.message}
                      initialSelectedItem={flight.endLocation}
                      onItemSelect={(itemSelected) => {
                        onItemSelect(`flights.${index}.endLocation`, itemSelected ?? undefined)
                      }}
                      ref={(elem: HTMLInputElement) => {
                        registerTabElement(elem)
                      }}
                    />

                    {/* Departure Date */}
                    <div className={getInputClass('multiDestDateWrapper')}>
                      <DatePicker
                        className='flight-dates'
                        title='Flights: Departure'
                        label='Departing'
                        mode='single'
                        numberOfMonths={isMobileComponent ? 12 : 2}
                        selected={getMultiDestinationSelectedDate(index, flights)}
                        errorMessage={getDateFieldErrorMessage(errors, 0, isRoundTrip(watchFlightType))}
                        handleCalendarChange={(dates) => {
                          onCalendarChange(dates, setDefaultMonth, setValue, index)
                        }}
                        disabled={{
                          before: getMultiDestDisabledDates(flights[index - 1]?.startDate),
                        }}
                        startMonth={currentDate}
                        endMonth={calendarMaxMonth}
                        defaultMonth={getMultiDestDefaultMonth(
                          flights[index - 1]?.startDate,
                          flight.startDate,
                        )}
                      />
                    </div>
                  </Fragment>
                ))}

                {/* Add another flight button */}
                <div className={cn('col-span-full', !useCompactLayout && 'lg:[grid-area:unset]')}>
                  <Button
                    type='secondary'
                    size='sm'
                    buttonType='button'
                    onClick={() => addFlightOnClick(setValue, watchFlights)}
                    disabled={watchFlights && watchFlights.length >= 5}
                  >
                    Add Another Flight
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Single/Round-trip flight inputs */}
                <div className={getInputClass('origin')}>
                  <AutocompleteWrapper
                    searchKey='startLocation'
                    label='Departing from?'
                    typeAheadEndpoint='flights'
                    errorMessage={errors.flights?.[0]?.startLocation?.message}
                    initialSelectedItem={flights[0]?.startLocation}
                    onItemSelect={(itemSelected) => {
                      onItemSelect('flights.0.startLocation', itemSelected ?? undefined)
                    }}
                    ref={(elem: HTMLInputElement) => {
                      registerTabElement(elem)
                    }}
                  />
                </div>

                <div className={getInputClass('destination')}>
                  <AutocompleteWrapper
                    searchKey='endLocation'
                    label='Going to?'
                    typeAheadEndpoint='flights'
                    initialSelectedItem={flights[0]?.endLocation}
                    errorMessage={errors.flights?.[0]?.endLocation?.message}
                    onItemSelect={(itemSelected) => {
                      onItemSelect('flights.0.endLocation', itemSelected ?? undefined)
                    }}
                    ref={(elem: HTMLInputElement) => {
                      registerTabElement(elem)
                    }}
                  />
                </div>

                <div className={getInputClass('timeline', watchFlightType || DEFAULT_VALUES.FLIGHT_TYPE)}>
                  <HorizonFlyDatePicker
                    key={watchFlightType}
                    className='flight-dates'
                    title={
                      (watchFlightType || DEFAULT_VALUES.FLIGHT_TYPE) === ROUND_TRIP
                        ? 'Flights: Departing – Returning'
                        : 'Flights: Departing'
                    }
                    label={
                      (watchFlightType || DEFAULT_VALUES.FLIGHT_TYPE) === ROUND_TRIP
                        ? 'Departing – Returning'
                        : 'Departing'
                    }
                    mode={(watchFlightType || DEFAULT_VALUES.FLIGHT_TYPE) === ROUND_TRIP ? 'range' : 'single'}
                    numberOfMonths={isMobileComponent ? 12 : 2}
                    selected={getSelectedDates(
                      0,
                      flights,
                      watchFlightType as FLIGHT_TYPE,
                      DEFAULT_VALUES.FLIGHT_TYPE,
                    )}
                    errorMessage={getDateFieldErrorMessage(
                      errors,
                      0,
                      isRoundTrip(watchFlightType as FLIGHT_TYPE),
                    )}
                    handleCalendarChange={(dates) => {
                      onCalendarChange(dates, setDefaultMonth, setValue, 0)
                      if (addHotel && dates && 'from' in dates && dates.from && dates?.to === undefined) {
                        setMaxSelectableDate(addDays(dates.from, 28))
                      } else {
                        setMaxSelectableDate(undefined)
                      }
                    }}
                    inputRef={registerTabElement}
                    flightDetails={flightDetails}
                    apolloClientInfo={{
                      clientName: appName,
                      clientVersion: appVersion,
                    }}
                    disabled={{
                      before: currentDate,
                      after: maxSelectableDate,
                    }}
                    startMonth={currentDate}
                    endMonth={calendarMaxMonth}
                    defaultMonth={defaultMonth}
                    min={addHotel ? 1 : 0}
                  />
                </div>
              </>
            )}

            {/* Room with Passengers /Passengers only Input (when hotel package is added) */}
            <div className={getInputClass('passengers', watchFlightType || DEFAULT_VALUES.FLIGHT_TYPE)}>
              <Controller
                control={control}
                name='travelers'
                defaultValue={watchTravelers}
                render={({ field }) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { ref, ...rest } = field
                  return (
                    <TravelerSelection
                      {...rest}
                      isHotel={addHotel}
                      roomsEnabled={addHotel}
                      tooltipErrorMessageId='travelerSelectionError'
                      errorMessage={errors?.travelers?.lapInfants?.message || ''}
                      initialValues={field.value}
                      shouldCountLapInfant
                      maxChildren={6}
                      isTravelerSelectionDisabled={isRebook || isModify}
                      onChange={(values: ITravelerInfo) => {
                        field.onChange({
                          adults: values.adults,
                          children: values.children,
                          lapInfants: values.lapInfants,
                          childrenAges: values.childrenAges,
                          rooms: values.rooms,
                        })
                      }}
                      collisionAvoidance={TRAVELER_SELECTION_COLLISION_AVOIDANCE}
                    />
                  )
                }}
              />
            </div>

            {/* Cabin Class - conditionally rendered */}
            {!isPackage && (
              <Select
                label={LABELS.CABIN_CLASS}
                isInset
                iconLeft='airline_seat_recline_normal'
                menuItems={[...CABIN_OPTIONS]}
                initialValue={watchCabinClass}
                placeholder={PLACEHOLDERS.CABIN_CLASS}
                className={getInputClass('cabin', watchFlightType, useCompactLayout)}
                onChange={(value: string) => {
                  setValue('cabinClass', value as CABIN_CLASS_TYPE, {
                    shouldDirty: true,
                  })
                }}
              />
            )}

            {/* Package Deals */}
            {watchFlightType === ROUND_TRIP && (
              <div className={getInputClass('packageDeals', watchFlightType, useCompactLayout)}>
                <HorizonBundleAndSave
                  productType='flights'
                  checkedValues={bundleSelections}
                  onSelectionChange={handleBundleSelectionChange}
                  tripType={watchTripType}
                />
              </div>
            )}

            {/* Search Button */}
            <Button
              type='primary'
              size='lg'
              buttonType='submit'
              className={cn(
                '[grid-area:unset]',
                !useCompactLayout && 'lg:[grid-area:unset]',
                watchFlightType === MULTI_DESTINATION ? 'sm:[grid-area:unset]' : 'sm:[grid-area:submit]',
                watchFlightType === ONE_WAY && !useCompactLayout && 'sm:col-span-full',
                isMobile ? 'fixed right-4 bottom-4 left-4' : '',
              )}
            >
              {PLACEHOLDERS.SEARCH_BUTTON}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
